// This is an improved version of utils/hooks/usePreventRouteChangeIf.ts, specifically tailored for Pengelolaan Kinerja.

/**
 * Cautions
 * If you use this hook directly on your page or on your template page
 * please be aware that this hook may break your code since next.js sometimes trigger
 * onRouteChange event when your route has params in it
 * https://github.com/vercel/next.js/issues/10875
 * To be safe, use this hook only after your page rendered (e.g wrap your page inside a component)
 */

import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'

type Props = {
  enabled: boolean
  bindPopState?: boolean

  onWarn?: () => void
  onBeforePopState?: () => void
}

const useWarn = (props: Props) => {
  const router = useRouter()

  const {
    enabled = false,
    bindPopState = false,
    onWarn,
    onBeforePopState,
  } = props

  const [isExitPrevented, setExitPrevented] = useState<boolean>(false)

  useEffect(() => {
    if (enabled) setExitPrevented(enabled)
  }, [enabled])

  const routeChangeStart = useCallback(
    (url: string) => {
      if (router.asPath !== url && isExitPrevented) {
        router.events.emit('routeChangeError')
        /* tslint:disable-next-line */
        onWarn?.()

        // Following is a hack-ish solution to abort a Next.js route change
        // as there's currently no official API to do so
        // See https://github.com/zeit/next.js/issues/2476#issuecomment-573460710
        // eslint-disable-next-line no-throw-literal
        throw `[Debug] Route change to "${url}" was aborted (this error can be safely ignored).`
      }
    },
    [router, isExitPrevented, onWarn]
  )

  const beforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if (isExitPrevented) {
        e.preventDefault()
        onWarn?.()
      }
    },
    [isExitPrevented, onWarn]
  )

  /**
   * Since beforeunload only support native browser dialog
   * so we bind the window event into simple function that return string
   * however on modern browser the string is not used on the dialog
   * reference: https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
   */
  const beforeUnloadNative = useCallback(
    (e: BeforeUnloadEvent) => {
      if (isExitPrevented) {
        return (e.returnValue = 'Are you sure you want to leave this page?')
      }
    },
    [isExitPrevented]
  )

  const beforePopStateCallback = useCallback(
    ({ url }) => {
      onBeforePopState?.()

      if (router.asPath !== url && isExitPrevented && bindPopState) {
        window.history.pushState(null, '', url)
        router.replace(router.asPath, router.asPath, { shallow: true })
        onWarn?.()
        return false
      }
      return true
    },
    [bindPopState, onBeforePopState, onWarn, router, isExitPrevented]
  )

  useEffect(() => {
    window.addEventListener('beforeunload', beforeUnloadNative)
    router.events.on('routeChangeStart', routeChangeStart)
    router.beforePopState(beforePopStateCallback)

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadNative)
      router.events.off('routeChangeStart', routeChangeStart)
      router.beforePopState(() => {
        return true
      })
    }
  }, [
    beforeUnloadNative,
    routeChangeStart,
    beforeUnload,
    router,
    beforePopStateCallback,
  ])

  return {
    // this should be called manually to remove listener ahead of time
    // of the react hook's callback cycle
    reset: () => {
      window.removeEventListener('beforeunload', beforeUnloadNative)
      router.events.off('routeChangeStart', routeChangeStart)

      setExitPrevented(false)

      router.beforePopState(() => {
        return true
      })
    },
  }
}

export default useWarn
